// Reading GET /assignments/{assignmentId}/submissions/status, and building the
// body for POST /assignments/{assignmentId}/jeddAIFeedback.
//
// All the derivation, none of the I/O — the same split submissionBatch.js keeps,
// so the progress screen, the student switcher and the open document all read one
// response through one set of rules.
//
// The vocabulary is the server's and deliberately provider-neutral: a row reports
// where it is in the pipeline, not which model is doing the work.

export const FEEDBACK_STATUS = {
  NOT_REQUESTED: 'NOT_REQUESTED',
  PENDING: 'PENDING',
  GENERATING: 'GENERATING',
  READY: 'READY',
  FAILED: 'FAILED',
};

// Finished, whichever way it went. A failed student is a reported outcome, not a
// row still owed something.
const SETTLED = new Set([FEEDBACK_STATUS.READY, FEEDBACK_STATUS.FAILED]);

// The only two a poll can still change. NOT_REQUESTED is neither settled nor in
// flight: nobody asked, so watching it would never end.
const IN_FLIGHT = new Set([FEEDBACK_STATUS.PENDING, FEEDBACK_STATUS.GENERATING]);

const rowsOf = (status) =>
  Array.isArray(status?.submissions) ? status.submissions : [];

export const feedbackStatusOf = (row) =>
  row?.feedback?.status || row?.feedbackStatus || FEEDBACK_STATUS.NOT_REQUESTED;

export const isFeedbackReady = (row) =>
  feedbackStatusOf(row) === FEEDBACK_STATUS.READY;

export const isFeedbackFailed = (row) =>
  feedbackStatusOf(row) === FEEDBACK_STATUS.FAILED;

export const isFeedbackInFlight = (row) => IN_FLIGHT.has(feedbackStatusOf(row));

const isSettledRow = (row) => SETTLED.has(feedbackStatusOf(row));

/** Every submission has finished, one way or the other. An unread status has not. */
export const isRunSettled = (status) => {
  const rows = rowsOf(status);
  return rows.length > 0 && rows.every(isSettledRow);
};

/** Something is still moving, so another read can tell us something new. */
export const isRunActive = (status) => rowsOf(status).some(isFeedbackInFlight);

/** Nothing to open: every student's feedback failed. Do not navigate on this. */
export const isRunAllFailed = (status) => {
  const rows = rowsOf(status);
  return rows.length > 0 && rows.every(isFeedbackFailed);
};

export const submissionIdOf = (row) => row?.submissionId || row?.id || null;

/**
 * The student to open first. Server order is Organise's routing order, so this is
 * the earliest-routed student who is actually readable — not whichever id happened
 * to come back first from the batch.
 */
export const firstReadySubmissionId = (status) =>
  submissionIdOf(rowsOf(status).find(isFeedbackReady));

export const findSubmissionRow = (status, submissionId) =>
  rowsOf(status).find(
    (row) => String(submissionIdOf(row)) === String(submissionId),
  ) || null;

export const runCounts = (status) => {
  const rows = rowsOf(status);
  return {
    total: rows.length,
    ready: rows.filter(isFeedbackReady).length,
    failed: rows.filter(isFeedbackFailed).length,
  };
};

/** Settled share, 0–1 — the progress bar's only input. */
export const runProgress = (status) => {
  const { total, ready, failed } = runCounts(status);
  return total === 0 ? 0 : (ready + failed) / total;
};

const failureClause = (failed) =>
  failed === 0
    ? null
    : `${failed} couldn't be marked`;

/** "12 of 30 ready · 1 couldn't be marked" */
export const runSummaryMessage = (status) => {
  const { total, ready, failed } = runCounts(status);
  if (total === 0) return '';
  return [`${ready} of ${total} ready`, failureClause(failed)]
    .filter(Boolean)
    .join(' · ');
};

const toFailure = (row) => ({
  submissionId: submissionIdOf(row),
  studentId: row?.studentId ?? null,
  studentName: row?.studentName || 'Student',
  reason: row?.feedback?.failureReason || 'Feedback could not be generated.',
});

/** The toast the client-side fan-out never showed: who was skipped, and why. */
export const failedStudents = (status) =>
  rowsOf(status).filter(isFeedbackFailed).map(toFailure);

/**
 * FE-BU19: exactly the rows a retry should carry.
 *
 * The run endpoint accepts a submissionIds subset, so a partly-failed run goes
 * back for the students it failed on and leaves the marked ones alone — re-running
 * the whole class would spend the work again and overwrite feedback already read.
 */
export const failedSubmissionIds = (status) =>
  rowsOf(status).filter(isFeedbackFailed).map(submissionIdOf).filter(Boolean);

export const unrequestedSubmissionIds = (status) =>
  rowsOf(status).filter(row => feedbackStatusOf(row) === FEEDBACK_STATUS.NOT_REQUESTED)
    .map(submissionIdOf).filter(Boolean);

// PENDING is also reported while OCR is running, before feedback is requested.
// A historical READY row cannot turn those conversions or untouched responses
// into an active feedback run.
export const bulkFeedbackPhase = (status) => {
  const rows = rowsOf(status);
  if (!rows.length) return 'unknown';
  if (rows.some(row => feedbackStatusOf(row) === FEEDBACK_STATUS.GENERATING)) return 'generating';
  if (rows.some(row => feedbackStatusOf(row) === FEEDBACK_STATUS.PENDING)) return 'converting';
  if (unrequestedSubmissionIds(status).length) return 'incomplete';
  if (rows.some(isFeedbackFailed)) return 'failed';
  return rows.every(isFeedbackReady) ? 'ready' : 'unknown';
};

const idSetOf = (ids) =>
  ids instanceof Set ? ids : new Set((ids || []).filter(Boolean).map(String));

/**
 * A reassigned student's existing row that this batch's commit routed the
 * teacher's re-upload into (round 4 C2, 06 §5.8). The server keeps it
 * RESUBMISSION_REQUESTED, with its old attempt's markers (feedbackRequestAcceptedAt,
 * jeddaiFeedbackReceivedAt, …), until its feedback is requested or it is
 * submitted, and lists it in the batch's submissionIds. Only that pairing counts:
 * a RESUBMISSION_REQUESTED row outside the batch is not this upload's to edit.
 * Pass the status row's feedback status where it is known: a run already moving
 * on the row (anything but NOT_REQUESTED) ends the redo.
 * Pass the batch read's `heldResubmissionIds` where the server gave it (round 4c
 * L2): the routed redos still awaiting hand-in. A routed row that was handed in
 * and then reassigned again is RESUBMISSION_REQUESTED and in the batch, yet no
 * longer this upload's redo; only the server can tell the two revisions apart.
 */
export const isBatchResubmission = (submission, batchSubmissionIds, feedbackStatus, heldResubmissionIds) => {
  const id = submission?.id || submission?.submissionId;
  return submission?.status === 'RESUBMISSION_REQUESTED' && !submission.locked &&
    Boolean(id) && idSetOf(batchSubmissionIds).has(String(id)) &&
    (heldResubmissionIds == null || idSetOf(heldResubmissionIds).has(String(id))) &&
    (feedbackStatus === undefined || feedbackStatus === FEEDBACK_STATUS.NOT_REQUESTED);
};

// Bulk review edits the uploaded draft only. Revising existing feedback belongs
// to the document's explicit revision flow, even if its status permits editing.
// The one exception is a batch resubmission (above): its old attempt's markers
// belong to the attempt the teacher's upload is replacing.
export const isBulkResponseEditable = (submission, batchSubmissionIds) =>
  isBatchResubmission(submission, batchSubmissionIds) ||
  (submission?.status === 'DRAFT' && !submission.locked &&
    !submission.feedbackRequestAcceptedAt && !submission.jeddaiFeedbackReceivedAt &&
    !submission.jeddaiFailureReason);

/**
 * A finished run with something still owed. Settled, because retrying while rows
 * are in flight would re-request students the server has not answered on yet.
 */
export const isRunRecoverable = (status) =>
  isRunSettled(status) && failedSubmissionIds(status).length > 0;

// ---------------------------------------------------------------------------
// Reconciling a rejected accept
// ---------------------------------------------------------------------------

const feedbackRecordOf = (row) =>
  row?.feedback ?? (row?.feedbackStatus ? { status: row.feedbackStatus } : null);

const sameRecord = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Whether the status endpoint shows the request took, whatever its accept said.
 *
 * The accept is one long POST. On a backend that fans the class out inline it
 * stays open for as long as the marking takes, and everything between the
 * browser and the handler is free to give up on it first — Cloud Run's request
 * timeout does at 300s, a proxy sooner. The server neither knows nor cares: it
 * keeps marking. So a rejected accept means "no receipt", not "refused", and
 * the rows are the receipt it never became.
 *
 * A row is evidence when it is in flight, or when it has settled into something
 * other than what it read when the request was made. A row the request never
 * saw before is evidence of nothing: a READY from an earlier run reads the same.
 *
 * @param {Object} status - the raw status, already narrowed to this batch
 * @param {Object} baseline - what the endpoint said when the request was made
 * @param {Map} [observed] - in-flight states seen since, keyed by submission id
 */
export const runStartedSince = (status, baseline, observed = new Map()) => {
  const before = new Map(
    rowsOf(baseline).map((row) => [submissionIdOf(row), feedbackRecordOf(row)]),
  );
  return rowsOf(status).some((row) => {
    if (isFeedbackInFlight(row)) return true;
    if (!isSettledRow(row)) return false;
    const id = submissionIdOf(row);
    if (!observed.has(id) && !before.has(id)) return false;
    const previous = observed.get(id) ??
      before.get(id) ?? { status: FEEDBACK_STATUS.NOT_REQUESTED };
    return !sameRecord(previous, feedbackRecordOf(row));
  });
};

/**
 * A refusal, in the teacher's words. A network-level failure arrives as a bare
 * TypeError — "Failed to fetch", "Load failed" — with no status, and that is
 * not a sentence anyone should be shown.
 */
export const runRefusalMessage = (error) => {
  const status = Number(error?.status);
  if (error instanceof TypeError && !(status > 0)) {
    return "We couldn't reach Jeddle to start marking. Check your connection and try again.";
  }
  return error?.message || 'Please try again.';
};

// ---------------------------------------------------------------------------
// POST /assignments/{assignmentId}/jeddAIFeedback body
// ---------------------------------------------------------------------------

const advancedMarkingOf = (selection, controls) =>
  controls || selection?.advancedMarking || null;

const shortAnswerIdOf = (selection) =>
  selection?.shortAnswerId || selection?.shortAnswerIds?.[0] || null;

const markingCriteriaIdOf = (selection) =>
  selection?.markingCriteriaId || selection?.markingCriteria?.id || null;

/**
 * The Tasks flow submits with no selection at all, so every tool resolves to null —
 * exactly what the per-submission endpoint already accepts for that path.
 */
export const buildFeedbackRunPayload = (
  selection,
  controls,
  { submissionIds, totalMarks } = {},
) => ({
  ...(submissionIds?.length ? { submissionIds } : {}),
  markingCriteriaId: markingCriteriaIdOf(selection),
  commentBankId: selection?.commentBankId || null,
  successCriteriaIds: selection?.successCriteriaIds || [],
  shortAnswerId: shortAnswerIdOf(selection),
  advancedMarking: advancedMarkingOf(selection, controls),
  ...(totalMarks != null ? { totalMarks: Number(totalMarks) } : {}),
});

// ---------------------------------------------------------------------------
// The handoff
// ---------------------------------------------------------------------------

/**
 * Where the teacher goes once the run hands off, or null when there is nothing to
 * open — every student failed, so the progress screen keeps the floor and names
 * them rather than pushing at an empty feedback panel.
 *
 * Shared because the wizard finishes on whichever route happens to be mounted:
 * the tasks page, the feedback panel, or /drafts/:assignmentId — which the wizard
 * navigates to itself at the Question step, and which is therefore the one that
 * is mounted most often.
 */

/** For independent teacher drafts (My Drafts) - goes to /documents/:id */
export const documentPath = (submissionId) =>
  submissionId ? `/documents/${submissionId}` : null;

/** For task submissions (class assignments) - goes to /submissions/:id */
export const submissionPath = (submissionId) =>
  submissionId ? `/submissions/${submissionId}` : null;

/** @deprecated Use openedSubmissionPath for tasks or documentPath for drafts */
export const openedDocumentPath = (response) =>
  documentPath(response?.firstReadySubmissionId);

/** For task bulk upload - navigates to submission view, not drafts */
export const openedSubmissionPath = (response) =>
  submissionPath(response?.firstReadySubmissionId);

/**
 * Where step 1 hands off when it creates the draft, and how the arrival is read.
 *
 * Two entry points make this jump — /documents/:id's popup and /drafts/:id's own —
 * and DraftWizardPage both reads the flag on arrival and scrubs it back off so a
 * refresh does not reopen a wizard the teacher closed. That is four sites spelling
 * out one route contract; they share it here instead.
 */
export const draftWizardLocation = (assignmentId) => ({
  pathname: `/drafts/${assignmentId}`,
  state: { openWizard: true },
});

/** Whether this navigation is the mid-flow handoff rather than a plain visit. */
export const isWizardArrival = (location) =>
  Boolean(location?.state?.openWizard);
